import { createRouter, createWebHashHistory, type RouteRecordRaw } from 'vue-router';
import { useAuthStore } from '@/stores/auth';

const routes: RouteRecordRaw[] = [
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/LoginView.vue'),
    meta: { layout: 'blank', title: '登录' },
  },
  {
    path: '/',
    component: () => import('@/components/AppLayout.vue'),
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        name: 'home',
        component: () => import('@/views/HomeView.vue'),
        meta: { title: '首页', icon: 'home' },
      },
      {
        path: 'seizure',
        name: 'seizure',
        component: () => import('@/views/SeizureListView.vue'),
        meta: { title: '病情记录', icon: 'pulse' },
      },
      {
        path: 'seizure/new',
        name: 'seizure-new',
        component: () => import('@/views/SeizureCreateView.vue'),
        meta: { title: '新增病情记录' },
      },
      {
        path: 'seizure/:id/edit',
        name: 'seizure-edit',
        component: () => import('@/views/SeizureCreateView.vue'),
        meta: { title: '编辑病情记录' },
      },
      {
        path: 'episode',
        name: 'episode',
        component: () => import('@/views/EpisodeView.vue'),
        meta: { title: '抽搐记录' },
      },
      {
        path: 'medication',
        name: 'medication',
        component: () => import('@/views/MedicationView.vue'),
        meta: { title: '用药分析', icon: 'pill' },
      },
      {
        path: 'report',
        name: 'report',
        component: () => import('@/views/ReportView.vue'),
        meta: { title: '报告信息' },
      },
      {
        path: 'imaging/ct',
        name: 'imaging-ct',
        component: () => import('@/views/CtListView.vue'),
        meta: { title: 'CT 列表', icon: 'scan', group: 'imaging' },
      },
      {
        path: 'imaging/mri',
        name: 'imaging-mri',
        component: () => import('@/views/MriListView.vue'),
        meta: { title: '核磁列表', icon: 'scan', group: 'imaging' },
      },
      {
        path: 'imaging/eeg',
        name: 'imaging-eeg',
        component: () => import('@/views/EegListView.vue'),
        meta: { title: '脑电列表', icon: 'wave', group: 'imaging' },
      },
      {
        path: 'knowledge',
        name: 'knowledge',
        component: () => import('@/views/KnowledgeView.vue'),
        meta: { title: '知识库', icon: 'book' },
      },
      {
        path: 'profile',
        name: 'profile',
        component: () => import('@/views/ProfileView.vue'),
        meta: { title: '个人中心', icon: 'person' },
      },
      {
        path: 'admin/users',
        name: 'admin-users',
        component: () => import('@/views/UserManageView.vue'),
        meta: { title: '用户管理', requiresAdmin: true },
      },
      {
        path: 'admin/drugs',
        name: 'admin-drugs',
        component: () => import('@/views/DrugManageView.vue'),
        meta: { title: '药品管理', requiresAdmin: true },
      },
      {
        path: 'profile/account',
        name: 'account-edit',
        component: () => import('@/views/AccountEditView.vue'),
        meta: { title: '编辑资料' },
      },
      {
        path: 'profile/edit',
        name: 'profile-edit',
        component: () => import('@/views/ProfileEditView.vue'),
        meta: { title: '编辑档案' },
      },
      {
        path: 'ai/chat',
        name: 'ai-chat',
        component: () => import('@/views/AiChatView.vue'),
        meta: { title: 'AI 助手', icon: 'ai' },
      },
      {
        path: 'ai/report',
        name: 'ai-report',
        component: () => import('@/views/AiReportView.vue'),
        meta: { title: 'AI 分析报告' },
      },
    ],
  },
  { path: '/:pathMatch(.*)*', redirect: '/' },
];

const router = createRouter({
  history: createWebHashHistory(),
  routes,
});

router.beforeEach((to) => {
  const auth = useAuthStore();
  if (!auth.user) auth.restore();

  if (to.meta.requiresAuth && !auth.isLoggedIn) {
    return { name: 'login', query: { redirect: to.fullPath } };
  }
  if (to.meta.requiresAdmin && auth.user?.role !== 'admin') {
    return { name: 'home' };
  }
  if (to.name === 'login' && auth.isLoggedIn) {
    return { name: 'home' };
  }
  return true;
});

router.afterEach((to) => {
  const base = (import.meta.env.VITE_APP_NAME as string | undefined) ?? 'KEEP HEALTH';
  const sub = (to.meta.title as string | undefined) ?? '';
  document.title = sub ? `${sub} · ${base}` : base;
});

export default router;
